import apiClient from "../../api/api";

/**
 * Creates a new project.
 *
 * @async
 * @function createProjectRequest
 * @param {Object} project - Payload containing project configuration data.
 * @returns {Promise<Object>} Created project data returned by the API.
 *
 * @example
 * await createProjectRequest({ name: "New Project", contactName: "Daniel Lima", isActive: true... });
 */
export const createProjectRequest = async(project) => {
    const createProjectResponse = await apiClient.post(import.meta.env.VITE_BASE_PROJECT_URL, project, {successToast:"Project created"})
    return createProjectResponse.data
}

/**
 * Retrieves the list of projects associated with a specific agent (user).
 *
 * @async
 * @function getAgentProjectsRequest
 * @param {number|string} userId - Unique identifier of the agent (user).
 * @param {boolean} showDisabled - Indicates whether disabled projects should be included.
 * @param {boolean} hasPrivilegedAccess - Indicates whether the user has privileged access to projects.
 * @returns {Promise<Array<Object>>} List of projects associated with the agent.
 *
 * @example
 * await getAgentProjectsRequest(1097, false, true);
 */
export const getAgentProjectsRequest = async(userId, showDisabled, hasPrivilegedAccess, page = 1, limit = 25) => {
    const getAgentProjectsReponse = await apiClient.get(import.meta.env.VITE_GET_AGENT_PROJECTS+`/${userId}?showDisabled=${showDisabled}&hasPrivilegedAccess=${hasPrivilegedAccess}&page=${page}&limit=${limit}`)
    return getAgentProjectsReponse.data
}

/**
 * Retrieves all agents (users) assigned to a specific project.
 *
 * @async
 * @function getAgentsForProjectRequest
 * @param {number|string} projectId - Unique identifier of the project.
 * @returns {Promise<Array<Object>>} List of agents assigned to the project.
 *
 * @example
 * await getAgentsForProjectRequest(16);
 */
export const getAgentsForProjectRequest = async(projectId) => {
    const getAgentsResponse = await apiClient.get(import.meta.env.VITE_BASE_PROJECT_URL+`/${projectId}/agents`)
    return getAgentsResponse.data
}

/**
 * Updates an existing project.
 *
 * @async
 * @function updateProjectRequest
 * @param {number|string} projectId - Unique identifier of the project.
 * @param {Object} project - Payload containing updated project data.
 * @returns {Promise<Object>} Updated project data returned by the API.
 *
 * @example
 * await updateProjectRequest(16, { name: "Updated Project Name" });
 */
export const updateProjectRequest = async(projectId, project) => {
    const updateProjectResponse = await apiClient.put(import.meta.env.VITE_BASE_PROJECT_URL+`/${projectId}`, project, {successToast:"Project updated"})
    return updateProjectResponse.data
}

/**
 * Assigns an agent (user) to a specific project.
 *
 * @async
 * @function assignAgentToProjectRequest
 * @param {number|string} projectId - Unique identifier of the project.
 * @param {number|string} userId - Unique identifier of the user (agent).
 * @param {Object} payload - Payload containing assignment configuration.
 * @returns {Promise<Object>} Assignment result returned by the API.
 *
 * @example
 * await assignAgentToProjectRequest(16, 1097, { roleId: 3 });
 */
export const assignAgentToProjectRequest = async(projectId, userId, payload) => {
    const assignAgentResponse = await apiClient.post(import.meta.env.VITE_BASE_PROJECT_URL+`/${projectId}/agents/${userId}`, payload, {successToast:"Agent assinged successfully"})
    return assignAgentResponse.data
}

/**
 * Removes an agent (user) from a specific project.
 *
 * @async
 * @function removeAgentFromProjectRequest
 * @param {number|string} projectId - Unique identifier of the project.
 * @param {number|string} userId - Unique identifier of the user (agent).
 * @returns {Promise<Object>} Removal confirmation returned by the API.
 *
 * @example
 * await removeAgentFromProjectRequest(16, 1097);
 */
export const removeAgentFromProjectRequest = async(projectId, userId) => {
    const removeAgentResponse = await apiClient.delete(import.meta.env.VITE_BASE_PROJECT_URL+`/${projectId}/agents/${userId}`, {successToast:"Agent removed from project"})
    return removeAgentResponse.data
}

/**
 * Updates the role of a user within a specific project.
 *
 * @async
 * @function updateUserRoleForProject
 * @param {number|string} projectId - Unique identifier of the project.
 * @param {number|string} userId - Unique identifier of the user (agent).
 * @param {number|string} roleId - New role identifier to be assigned.
 * @returns {Promise<Object>} Updated user role data returned by the API.
 *
 * @example
 * await updateUserRoleForProject(16, 1097, 2);
 */
export const updateUserRoleForProject = async(projectId, userId, roleId) => {
    const updateUserProjectRoleReponse = await apiClient.put(import.meta.env.VITE_BASE_PROJECT_URL+`/${projectId}/agents/${userId}?roleId=${roleId}`, {successToast:"User role was updated"})
    
    return updateUserProjectRoleReponse.data
}

/**
 * Retrieves the weekly report for a specific agent within a project.
 *
 * @async
 * @function getAgentWeeklyReport
 * @param {number|string} projectId - Unique identifier of the project.
 * @param {number|string} agentId - Unique identifier of the agent.
 * @returns {Promise<Object>} Weekly agent report data.
 *
 * @example
 * await getAgentWeeklyReport(16, 1097);
 */
export const getAgentWeeklyReport = async(projectId, agentId) => {
    const getAgentReportResponse = await apiClient.get(import.meta.env.VITE_BASE_PROJECT_URL+`/${projectId}/reports/weekly/agents/${agentId}`)
    
    return getAgentReportResponse.data
}

/**
 * Retrieves all project roles available in the system.
 *
 * @async
 * @function getProjectRolesRequest
 * @returns {Promise<Array<Object>>} List of project roles.
 *
 * @example
 * const roles = await getProjectRolesRequest();
 */
export const getProjectRolesRequest = async() => {
    const getRolesResponse = await apiClient.get(import.meta.env.VITE_BASE_PROJECT_ROLES_ENDPOINT)
   
    return getRolesResponse.data
}

/**
 * Creates a new project role.
 *
 * @async
 * @function createProjectRoleRequest
 * @param {Object} role - Payload containing project role data.
 * @returns {Promise<Object>} Created project role data returned by the API.
 *
 * @example
 * await createProjectRoleRequest({ name: "Team Lead" });
 */
export const createProjectRoleRequest = async(role) => {
    const createRoleReponse = await apiClient.post(import.meta.env.VITE_BASE_PROJECT_ROLES_ENDPOINT, role, {successToast: "New role created"})
    
    return createRoleReponse.data
}

/**
 * Updates an existing project role.
 *
 * @async
 * @function updateProjectRoleRequest
 * @param {number|string} roleId - Unique identifier of the project role.
 * @param {Object} role - Payload containing updated role data.
 * @returns {Promise<Object>} Updated project role data returned by the API.
 *
 * @example
 * await updateProjectRoleRequest(5, { name: "Senior Developer" });
 */
export const updateProjectRoleRequest = async(roleId, role) => {
    const updateProjectRoleResponse = await apiClient.put(import.meta.env.VITE_BASE_PROJECT_ROLES_ENDPOINT+`/${roleId}`, role, {successToast:"Projet role was updated"})
    
    return updateProjectRoleResponse.data
}

/**
 * Deletes a project role by its unique identifier.
 *
 * @async
 * @function deleteProjectRoleRequest
 * @param {number|string} roleId - Unique identifier of the project role.
 * @returns {Promise<Object>} API response confirming deletion.
 *
 * @example
 * await deleteProjectRoleRequest(5);
 */
export const deleteProjectRoleRequest = async(roleId) => {
    const deleteProjectRoleResponse = await apiClient.delete(import.meta.env.VITE_BASE_PROJECT_ROLES_ENDPOINT+`/${roleId}`, {successToast: "Project role was deleted"})
    
    return deleteProjectRoleResponse.data
}

/**
 * Retrieves all pending requests associated with a specific project.
 *
 * @async
 * @function getProjectPendingRequest
 * @param {number|string} projectId - Unique identifier of the project.
 * @returns {Promise<Array<Object>>} List of pending requests for the project.
 *
 * @example
 * await getProjectPendingRequest(16);
 */
export const getProjectPendingRequest = async(projectId) => {
    const getRequestsResposne = await apiClient.get(import.meta.env.VITE_BASE_PROJECT_URL+`/${projectId}/requests/pending`, {timeout: 90000})
    return getRequestsResposne.data
}

export const getProjects = async(page = 1, pageZise = 100, disabled = false) => {

    const response = await apiClient.get(import.meta.env.VITE_BASE_PROJECT_URL+`?page=${page}&limit=${pageZise}&isDisabled=false`)

    return response.data
}